import { Component, Injectable } from '@angular/core';
import { ItemDetailsService } from '../services/item-details.service';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { Observable, Subscription, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { finalize } from 'rxjs/operators';
import { ProductService } from '../pages/service/product.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { OrderDraftService } from '../services/order-draft.service';
import { StaffDisputeService } from '../services/staff-dispute.service';
import { StaffQuotationService } from '../services/staff-quotation.service';
import { InMemoryStorageService } from '../services/in-memory-storage.service';
import { AddOrderHeaderRequest, BulkOrderLineRequest, ResolvedAddressPayload } from '../services/item-details.models';
import {
    clampDiscountPercent,
    round2,
    isCreditNoteOrder,
    normalizeCreditNoteQuantity,
    calculateRowAmounts,
    resolveCreditLimit3,
    extractNoDecPl,
    resolveQtyDecimalPlaces,
    resolveLineNoDecPl,
    resolveDivisionMarginFromItemDetailsResponse,
    logDivisionMarginResolution,
    mapShipQtyFields,
    effectiveShippedQty,
    hasPersistedShipQty
} from './item-utils';
import { getBearerToken } from '../utils/auth-request.util';
import { RefreshTokenService } from '../services/refresh-token.service';
import { MarginValidationService } from '../services/margin-validation.service';
@Injectable({ providedIn: 'root' })
@Component({
    selector: 'app-item-order-handler',
    template: '',
    providers: [ProductService, MessageService]
})
export class ItemOrderHandlerService {
    private itemDetailsSub?: Subscription;
    private itemDetailsInFlightKey: string | null = null;

    constructor(
        private itemDetailsService: ItemDetailsService,
        private messageService: MessageService,
        private router: Router,
        private http: HttpClient,
        private orderDraftService: OrderDraftService,
        private staffDisputeService: StaffDisputeService,
        private staffQuotationService: StaffQuotationService,
        private inMemoryStorageService: InMemoryStorageService,
        private marginValidationService: MarginValidationService,
        private productService: ProductService,
        private refreshTokenService: RefreshTokenService
    ) {}

    private resolveAddressPayload(item: any, product: any): ResolvedAddressPayload {
        const isCustom =
            Number(item?.isCustom) === 1 ||
            Number(product?.isCustom) === 1 ||
            item?.isCustom === '1' ||
            product?.isCustom === '1';
        const customAddr = (item?.customAddress ?? product?.customAddress ?? '').toString().trim();
        let directAddressId = (item?.addressId || product?.addressId || '').toString().trim();
        // Synthetic picker ids from empty M3 ADID must never be persisted.
        if (directAddressId.startsWith('__index_')) {
            directAddressId = '';
        }
        const resolvedAddressId = isCustom
            ? ''
            : directAddressId || (/^#\S+/.test(customAddr) ? customAddr : '');

        const payload: ResolvedAddressPayload = {
            addressId: resolvedAddressId,
            customerAddress: isCustom ? '' : item?.customerAddress || product?.customerAddress || '',
            isCustom: isCustom ? 1 : 0
        };
        if (isCustom) {
            payload.customAddress = customAddr;
        }
        return payload;
    }

    /** Prevents parallel AddOrderHeader for new drafts (double-click / slow network). */
    private createHeaderInFlight = false;

    submitOrder(ctx: {
        item: any;
        itemList: any[];
        company: string;
        division: string;
        product: any;
        salesPerson: string;
        loadingSetter: (val: boolean) => void;
        loaderMessageSetter: (l1: string, l2: string) => void;
        getTotalValue: () => number;
        getTotalDiscountValue: () => number;
        getTotalNetAmount: () => number;
        getTotalVatAmount: () => number;
        getTotalWeight: () => number;
        getSubscription: () => number;
        uca2?: string;
        setCustomerCreditBlockAfterSave?: boolean;
        onOrderLinesSubmitted: (referenceOrderNumber: string, orderNumber: string) => void;
        creditNoteFromDispute?: {
            claimNumber: string;
            sourceOrderNumber: string;
            creditOrderTypeId: string;
        } | null;
        punchFromQuotation?: {
            quotationNumber: string;
            customerId: string;
            warehouse: string;
        } | null;
    }) {
        if (!ctx.itemList || ctx.itemList.length === 0) {
            ctx.loadingSetter(false);
            this.messageService.add({
                severity: 'error',
                summary: 'No Articles to Add',
                detail: 'Header cannot be added as there is no Article to add. Please add Articles before submitting.',
                life: 3000
            });
            return;
        }
        console.log('Submitting order with item:', ctx.item);

        if (ctx.setCustomerCreditBlockAfterSave) {
            ctx.loadingSetter(true);
            ctx.loaderMessageSetter('Updating customer status...', 'Please wait...');
            this.setCustomerCreditBlockBeforeSave(ctx).subscribe({
                next: () => this.continueSubmitOrder(ctx),
                error: () => this.continueSubmitOrder(ctx)
            });
            return;
        }

        this.continueSubmitOrder(ctx);
    }

    private continueSubmitOrder(ctx: {
        item: any;
        itemList: any[];
        company: string;
        division: string;
        product: any;
        salesPerson: string;
        loadingSetter: (val: boolean) => void;
        loaderMessageSetter: (l1: string, l2: string) => void;
        getTotalValue: () => number;
        getTotalDiscountValue: () => number;
        getTotalNetAmount: () => number;
        getTotalVatAmount: () => number;
        getTotalWeight: () => number;
        getSubscription: () => number;
        uca2?: string;
        onOrderLinesSubmitted: (referenceOrderNumber: string, orderNumber: string) => void;
    }) {
        const proceedToOrderLines = () => {
            ctx.onOrderLinesSubmitted(ctx.item.referenceOrderId || '', ctx.item.orderNumber);
        };

        // If order header already exists, optionally update UCA2 then submit lines
        if (ctx.item.orderNumber && ctx.item.orderNumber !== 'New Order') {
            console.log(ctx.item.referenceOrderId, ctx.item.orderNumber);

            if (ctx.uca2 !== undefined) {
                ctx.loadingSetter(true);
                ctx.loaderMessageSetter('Updating order information...', 'Please wait...');
                this.itemDetailsService.updateOrderHeaderUca2(ctx.item.orderNumber, ctx.company, ctx.division, ctx.uca2).subscribe({
                    next: () => {
                        ctx.item.uca2 = ctx.uca2;
                        proceedToOrderLines();
                    },
                    error: (error: any) => {
                        ctx.loadingSetter(false);
                        const errormsg =
                            typeof error?.error === 'string'
                                ? error.error
                                : error?.error?.message || 'Failed to update extra order information. Please try again.';
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error Updating UCA2',
                            detail: errormsg,
                            life: 3000
                        });
                    }
                });
                return;
            }

            proceedToOrderLines();
            return;
        }

        const isCustomPre =
            Number(ctx.item?.isCustom) === 1 ||
            Number(ctx.product?.isCustom) === 1 ||
            ctx.item?.isCustom === '1' ||
            ctx.product?.isCustom === '1';
        const customAddrPre = (ctx.item?.customAddress ?? ctx.product?.customAddress ?? '').toString().trim();
        if (isCustomPre && !customAddrPre) {
            ctx.loadingSetter(false);
            this.messageService.add({
                severity: 'error',
                summary: 'Custom address required',
                detail: 'Please enter a custom delivery address before submitting.',
                life: 4000
            });
            return;
        }

        if (this.createHeaderInFlight) {
            console.warn('Ignoring duplicate AddOrderHeader while a create is already in flight');
            ctx.loadingSetter(false);
            return;
        }
        this.createHeaderInFlight = true;

        ctx.loadingSetter(true);
        ctx.loaderMessageSetter('Submitting order...', 'Please wait...');
        console.log('Submitting order with item:', ctx.item);
        console.log('this.item.addressId:', ctx.item.addressId);
        console.log('this.product.addressId:', ctx.product.addressId);
        console.log('this.item.addressId:', ctx.item.address);
        console.log('this.product.address:', ctx.product.address);

        const addressFields = this.resolveAddressPayload(ctx.item, ctx.product);

        const headerBody: AddOrderHeaderRequest = {
            orderId: ctx.item.orderId || '',
            companyId: ctx.company,
            divisionId: ctx.division,
            customerId: ctx.item.customer || ctx.product.customer || ctx.item.customerId || '',
            addressId: addressFields.addressId,
            orderTypeId: ctx.item.orderTypeId || ctx.product.orderTypeId || ctx.product.orderType || '',
            referenceOrderId: ctx.item.referenceOrderId || ctx.product.referenceOrderId || '',
            status: ctx.item.status || ctx.product.status || 20,
            // status: 20,

            facilityId: ctx.item.facilityId || ctx.product.facilityId || ctx.product.facility || '',
            whsId: ctx.item.warehouseId || ctx.product.wharehouse || ctx.product.warehouseId || ctx.item.whsId || '',
            phoneNumber: ctx.item.phoneNumber || '',
            email: ctx.item.mail || ctx.product.mail || '',
            vatRegistrationNo: ctx.item.vatRegistrationNo || ctx.product.vatRegistrationNo || '',
            customerAddress: addressFields.customerAddress,
            requestedDate: ctx.item.requestedDate || ctx.product.requestedDate || '',
            userIdResponsible: ctx.salesPerson,
            userEmailResponsible: (this.inMemoryStorageService.get('email') || '').trim(),
            customerName:
                ctx.item.customerName ||
                ctx.product.customerName ||
                ctx.product.name ||
                ctx.item.name ||
                '',
            outstanding: ctx.item.outstanding || ctx.product.outstanding || '',
            totalOverdue: ctx.item.totalOverdue || ctx.product.totalOverdue || '',
            creditLimit3: String(
                resolveCreditLimit3({
                    okcrl3: ctx.item.okcrl3 ?? ctx.product.okcrl3,
                    creditLimit3: ctx.item.creditLimit3 ?? ctx.product.creditLimit3
                })
            ),
            grossValue: ctx.getTotalValue().toFixed(2),
            totalDiscountValue: ctx.getTotalDiscountValue().toFixed(2),
            netAmount: ctx.getTotalNetAmount().toFixed(2),
            vatAmount: ctx.getTotalVatAmount().toFixed(2),
            orderValue: ctx.getSubscription().toFixed(2),
            isCustom: addressFields.isCustom,
            orderMcm:
                Number(
                    ctx.product?.orderMcm ??
                        ctx.item?.orderMcm ??
                        ctx.product?.minimumOrderMargin ??
                        ctx.item?.minimumOrderMargin ??
                        ctx.product?.F1N096
                ) || undefined,
            orderCost: this.marginValidationService.calculateOrderContributionMargin(ctx.itemList || []).totalOrderCost
        };
        if (addressFields.customAddress) {
            headerBody.customAddress = addressFields.customAddress;
        }
        if (ctx.uca2) {
            headerBody.uca2 = ctx.uca2;
        }
        console.log('Header Body:', headerBody);
        console.log('Header Body:', headerBody.addressId);

        this.itemDetailsService.addOrderHeader(headerBody, ctx.company, ctx.division).subscribe({
            next: (headerResponse: any) => {
                this.createHeaderInFlight = false;
                if (headerResponse && headerResponse['Order Number']) {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Item Header Added',
                        detail: 'Order header has been successfully added.',
                        life: 1000
                    });
                    localStorage.removeItem('orders');
                    localStorage.removeItem('routeParams');
                    localStorage.removeItem('orderLines');
                    this.orderDraftService.clearDraft();
                    const createdOrderNumber = headerResponse['Order Number'];
                    ctx.item.orderNumber = createdOrderNumber;
                    this.linkDisputeCreditNoteIfNeeded(ctx, createdOrderNumber);
                    this.linkQuotationPunchIfNeeded(ctx, createdOrderNumber);
                    if (ctx.uca2) {
                        ctx.item.uca2 = ctx.uca2;
                    }
                    this.itemDetailsService.flushPendingPrePostTexts(createdOrderNumber, ctx.company, ctx.division).subscribe({
                        next: (results) => {
                            if (results.length) {
                                this.messageService.add({
                                    severity: 'success',
                                    summary: 'Pre/Post Text Saved',
                                    detail: `${results.length} pending header/line text entries saved.`,
                                    life: 2500
                                });
                            }
                        },
                        error: () => {
                            // Non-blocking: order save must continue even if pending text flush fails.
                        }
                    });
                    ctx.onOrderLinesSubmitted(headerResponse['Reference Order Number'], headerResponse['Order Number']);
                } else {
                    ctx.loadingSetter(false);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error Adding Header',
                        detail: 'Failed to add order header. Please try again.',
                        life: 2000
                    });
                }
            },
            error: (error: any) => {
                console.log('Error adding order header:', error);

                this.createHeaderInFlight = false;
                ctx.loadingSetter(false);
                let errormsg = error && error.error && error.error.addressId ? error.error.addressId : 'Failed to add order header. Please try again.';
                this.messageService.add({
                    severity: 'error',
                    summary: errormsg,
                    detail: 'Failed to add order header. Please try again.',
                    life: 3000
                });
            }
        });
    }

    private linkDisputeCreditNoteIfNeeded(
        ctx: {
            creditNoteFromDispute?: {
                claimNumber: string;
                creditOrderTypeId: string;
            } | null;
            company: string;
            division: string;
            item: any;
            product: any;
        },
        createdOrderNumber: string
    ): void {
        const link = ctx.creditNoteFromDispute;
        if (!link?.claimNumber || !createdOrderNumber) {
            return;
        }
        const whsId =
            ctx.item?.whsId ||
            ctx.item?.warehouseId ||
            ctx.product?.whsId ||
            ctx.product?.warehouseId ||
            this.inMemoryStorageService.get('warehouseId') ||
            this.inMemoryStorageService.get('whsId') ||
            this.inMemoryStorageService.get('warehouse') ||
            '';
        if (!whsId) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Credit note not linked',
                detail: 'Order saved but dispute link failed: warehouse scope missing.',
                life: 5000
            });
            return;
        }
        this.staffDisputeService
            .linkCreditNote(link.claimNumber, {
                companyId: ctx.company,
                divisionId: ctx.division,
                whsId: String(whsId)
            }, {
                creditNoteOrderNumber: createdOrderNumber,
                creditNoteOrderType: link.creditOrderTypeId
            })
            .subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Dispute linked',
                        detail: `Credit note ${createdOrderNumber} linked to claim ${link.claimNumber}.`,
                        life: 4000
                    });
                },
                error: (err) => {
                    const detail =
                        (err?.error && typeof err.error === 'object' && err.error.message) ||
                        (typeof err?.error === 'string' ? err.error : '') ||
                        'Order was saved but linking to the dispute failed. Link manually from dispute review.';
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Link failed',
                        detail: String(detail),
                        life: 6000
                    });
                }
            });
    }

    private linkQuotationPunchIfNeeded(
        ctx: {
            punchFromQuotation?: {
                quotationNumber: string;
                warehouse?: string;
            } | null;
            company: string;
            division: string;
            item: any;
            product: any;
        },
        createdOrderNumber: string
    ): void {
        const link = ctx.punchFromQuotation;
        if (!link?.quotationNumber || !createdOrderNumber) {
            return;
        }
        const whsId =
            link.warehouse ||
            ctx.item?.whsId ||
            ctx.item?.warehouseId ||
            ctx.product?.whsId ||
            ctx.product?.warehouseId ||
            this.inMemoryStorageService.get('warehouseId') ||
            this.inMemoryStorageService.get('whsId') ||
            this.inMemoryStorageService.get('warehouse') ||
            '';
        if (!whsId) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Draft order not linked',
                detail: 'Order saved but draft order link failed: warehouse scope missing.',
                life: 5000
            });
            return;
        }
        this.staffQuotationService
            .linkPunchedOrder(
                link.quotationNumber,
                {
                    companyId: ctx.company,
                    divisionId: ctx.division,
                    whsId: String(whsId)
                },
                { punchedOrderNumber: createdOrderNumber }
            )
            .subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Draft order processed',
                        detail: `Order ${createdOrderNumber} linked to ${link.quotationNumber}.`,
                        life: 4000
                    });
                },
                error: (err) => {
                    const detail =
                        (err?.error && typeof err.error === 'object' && err.error.message) ||
                        (typeof err?.error === 'string' ? err.error : '') ||
                        'Order was saved but linking to the draft order failed.';
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Link failed',
                        detail: String(detail),
                        life: 6000
                    });
                }
            });
    }

    submitOrderLines(ctx: {
        itemList: any[];
        clearDeletedItems: () => void;
        company: string;
        division: string;
        product?: any;
        item?: any;
        loadingSetter: (val: boolean) => void;
        messageHandler: (msg: { severity: string; summary: string; detail: string; life?: number }) => void;
        routerNavigate: (commands: any[]) => void;
        getTotalValue: () => number;
        getTotalDiscountValue: () => number;
        getTotalNetAmount: () => number;
        getTotalVatAmount: () => number;
        getTotalWeight: () => number;
        getSubscription: () => number;
        orderType: string;
        requestedDate: string;
        totalArticles: number;
        orderNumber: string;
        referenceOrderNumber: string;
        info?: any;
        /** Return true to skip the default /Orders1 redirect after bulk lines succeed. */
        onBulkLinesSuccess?: (orderNumber: string) => boolean | void;
    }) {
        ctx.loadingSetter(true);
        console.log('Setting info for each line:', ctx.info);

        // Ensure each line.info is set from ctx.info if not already present
        if (ctx.info) {
            console.log('Setting info for each line:', ctx.info);

            (ctx.itemList || []).forEach((line) => {
                if (!line.info || Object.keys(line.info).length === 0) {
                    line.info = { ...ctx.info };
                }
            });
        }
        console.log('Submitting order lines with itemList:', ctx.itemList);

        // Filter out lines with status 20 (already submitted)
        const linesToSubmit = (ctx.itemList || []).filter((line) => line.status);

        if (!linesToSubmit.length) {
            ctx.loadingSetter(false);
            ctx.messageHandler({
                severity: 'error',
                summary: 'No Articles to Add',
                detail: 'There are no Articles to add. Please add Articles before submitting.',
                life: 3000
            });
            return;
        }

        const creditNote = isCreditNoteOrder(ctx.division, ctx.orderType || '');

        // Prepare the payload as an array
        const orderLineDto = linesToSubmit.map((line) => {
            const itemDetails = line.info || {};
            if (creditNote) {
                line.quantity = normalizeCreditNoteQuantity(Number(line.quantity));
                Object.assign(line, calculateRowAmounts(line));
            }
            const unitCost =
                Number(line?.cost ?? line?.costPrice) ||
                Number(itemDetails?.MCCSU1 ?? itemDetails?.mccsu1) ||
                0;
            const itemMcm =
                Number(line?.minimumMargin ?? line?.itemMcm) ||
                Number(itemDetails?.MFLAMA ?? itemDetails?.mflama) ||
                0;
            const qtyForCalc = effectiveShippedQty(line);
            const lineCost = round2(unitCost * qtyForCalc);
            const sellingAmount = this.marginValidationService.resolveLineSellingAmount(line);
            const itemMargin = this.marginValidationService.calculateItemContributionMargin(
                unitCost,
                qtyForCalc,
                sellingAmount,
                itemMcm
            );
            console.log('[MARGIN-DIVISION] submitOrderLines payload line', {
                division: ctx.division,
                itemNo: line.itemNumber,
                lineNo: line.lineNumber,
                costPriceSentToDb: unitCost,
                itemMcmSentToDb: itemMcm,
                lineCostSentToDb: lineCost,
                rowCost: line.cost,
                rowMinimumMargin: line.minimumMargin
            });
            return {
                company: ctx.company,
                division: ctx.division,
                orderId: line.orderLineId || null,
                orderNumber: ctx.orderNumber,
                referenceOrderNumber: ctx.referenceOrderNumber || '',
                lineNo: line.lineNumber,
                itemNo: line.itemNumber,
                itemName: line.itemName,
                qty: line.quantity,
                vat: line.vat,
                discount: clampDiscountPercent(line.discount),
                netAmount: line.amountAfterDiscount,
                grossAmount: line.amount,
                vatAmount: line.vatAmount,
                vatPersentage: line.vat,
                discountAmount:line.discountAmount,
                lineAmount: line.amountAfterVat || line.vatAmount + line.amountAfterDiscount,
                rate: line.rate,
                status: 20,
                costPrice: unitCost,
                lineCost,
                itemMcm,
                calculatedMargin: itemMargin.actualMarginPercent,
                vatCode: line.vatCode ?? '',
                grossWeight: line.grossWeight ?? '',
                netWeight: line.netWeight ?? '',
                pcs: line.pcs ?? '',
                size: line.size ?? '',
                itemGroup: line.itemGroup || '',
                noDecPl: resolveQtyDecimalPlaces(line.noDecPl, 2),
                recommendationWasRecommended: (() => {
                    const v = line.recommendationWasRecommended;
                    if (v === true || v === 'true' || v === '1') {
                        return 'true';
                    }
                    if (v === false || v === 'false' || v === '0') {
                        return 'false';
                    }
                    if (typeof v === 'string' && v.trim().toUpperCase() === 'NO_RECO') {
                        return 'NO_RECO';
                    }
                    return undefined;
                })(),
                recommendationAddSource: line.recommendationAddSource || undefined,
                recommendationRank:
                    line.recommendationRank != null && Number.isFinite(Number(line.recommendationRank))
                        ? Number(line.recommendationRank)
                        : undefined,
                recommendationRunId: line.recommendationRunId || undefined
            };
        });
console.log(ctx.orderType, ctx.requestedDate);

        const orderMargin = this.marginValidationService.calculateOrderContributionMargin(ctx.itemList || []);
        const addressFields = this.resolveAddressPayload(ctx.item, ctx.product);
        const payload: BulkOrderLineRequest = {
            orderNumber: ctx.orderNumber,
            company: ctx.company,
            division: ctx.division,
            grossValue: ctx.getTotalValue().toFixed(2),
            totalDiscountValue: ctx.getTotalDiscountValue().toFixed(2),
            netAmount: ctx.getTotalNetAmount().toFixed(2),
            vatAmount: ctx.getTotalVatAmount().toFixed(2),
            totalWeight: ctx.getTotalWeight().toFixed(2),
            orderValue: ctx.getSubscription().toFixed(2),
            orderType: ctx.orderType || '',
            requestedDate: ctx.requestedDate || '',
            totalArticles: ctx.totalArticles,
            orderCost: orderMargin.totalOrderCost,
            orderMcm:
                Number(
                    ctx.product?.orderMcm ??
                        ctx.item?.orderMcm ??
                        ctx.product?.minimumOrderMargin ??
                        ctx.item?.minimumOrderMargin ??
                        ctx.product?.F1N096
                ) || undefined,
            addressId: addressFields.addressId,
            customerAddress: addressFields.customerAddress,
            isCustom: addressFields.isCustom,
            orderLineDto
        };
        if (addressFields.customAddress) {
            payload.customAddress = addressFields.customAddress;
        }

        this.itemDetailsService.uploadBulkOrderLines(payload, ctx.company, ctx.division).subscribe({
            next: (response: any) => {
                localStorage.removeItem('orders');
                localStorage.removeItem('routeParams');
                localStorage.removeItem('orderLines');
                // Mark all submitted lines as status 20+
                linesToSubmit.forEach((line) => (line.status = 20));
                ctx.loadingSetter(false);
                ctx.messageHandler({
                    severity: 'success',
                    summary: 'Articles Added',
                    detail: `${linesToSubmit.length} Articles have been successfully added.`,
                    life: 3000
                });
                ctx.clearDeletedItems();
                setTimeout(() => {
                    const handled = ctx.onBulkLinesSuccess?.(ctx.orderNumber) === true;
                    if (!handled) {
                        ctx.routerNavigate(['/Orders1']);
                    }
                }, 500);
            },
            error: (error: any) => {
                console.log('Error while submitting order lines:', error.Status);

                let errorMsg = 'Failed to add order lines. Please try again.';
                let orderNumber = '';
                let status = '';
                try {
                    if (error && error.error) {
                        const parsed = typeof error.error === 'string' ? JSON.parse(error.error) : error.error;
                        console.log('Parsed error response:', parsed);
                        status = parsed.Status;
                        console.log('Parsed status:', status);
                        if (parsed.Message) {
                            errorMsg = parsed.Message;
                            console.log('Parsed error message:', errorMsg);
                            console.log('Parsed status:', status);
                        }
                        if (parsed['Order Number']) {
                            orderNumber = parsed['Order Number'];
                        }
                    }
                } catch (e) {
                    errorMsg = error.message || errorMsg;
                }

                ctx.loadingSetter(false);

                if (status === '15') {
                    // Show info message for status 15
                    ctx.messageHandler({
                        severity: 'success',
                        summary: 'Articles Added Successfully',
                        detail: 'Articles added successfully.',
                        life: 2000
                    });
                    // Also show the error details as error
                    ctx.messageHandler({
                        severity: 'error',
                        summary: orderNumber ? `Order Number: ${orderNumber}` : 'Error Adding Articles',
                        detail: errorMsg,
                        life: 3000
                    });
                    localStorage.removeItem('orders');
                    localStorage.removeItem('routeParams');
                    localStorage.removeItem('orderLines');
                    setTimeout(() => {
                        ctx.routerNavigate(['/Orders1']);
                    }, 500);
                } else {
                    // Handle all other errors
                    ctx.messageHandler({
                        severity: 'error',
                        summary: orderNumber ? `Order Number: ${orderNumber}` : 'Error Adding Articles',
                        detail: errorMsg,
                        life: 3000
                    });
                }
            }
        });
    }

    /** Before draft save: set M3 BLCD=2 when user confirmed over-limit Save as Draft. */
    private setCustomerCreditBlockBeforeSave(ctx: {
        item: any;
        product: any;
        company: string;
        division: string;
    }) {
        const cuno = String(
            ctx.item?.customerId ||
                ctx.item?.customer ||
                ctx.product?.customerId ||
                ctx.product?.customer ||
                ''
        ).trim();

        const callBlock = () =>
            this.productService.applyCustomerCreditBlockOnDraftSave({
                cuno,
                cono: ctx.company,
                divi: ctx.division,
                divisionId: ctx.division,
                referenceOrderId: ctx.item?.referenceOrderId ?? ctx.product?.referenceOrderId,
                creditApprovedSource: ctx.item ?? ctx.product
            });

        if (getBearerToken()) {
            return callBlock();
        }

        return from(this.refreshTokenService.refreshToken()).pipe(switchMap(() => callBlock()));
    }

    fetchOrderLines(ctx: {
        item: any;
        company: string;
        division: string;
        loadingSetter: (val: boolean) => void;
        loaderMessageSetter: (l1: string, l2: string) => void;
        itemListSetter: (list: any[]) => void;
        messageHandler: (msg: { severity: string; summary: string; detail: string; life?: number }) => void;
    }) {
        if (!ctx.item || !ctx.item.orderNumber) {
            ctx.loadingSetter(false);
            ctx.messageHandler({
                severity: 'error',
                summary: 'Missing Required Fields',
                detail: 'Order number is missing. Please check the order header before proceeding.',
                life: 3000
            });
            return;
        }

        ctx.loadingSetter(true);
        ctx.loaderMessageSetter('Fetching order lines...', 'Please wait...');

        this.itemDetailsService.getOrderLines(ctx.item.orderNumber, ctx.company, ctx.division).subscribe({
            next: (response: any) => {
                ctx.loadingSetter(false);
                    console.log('Order lines API response:', response);

            const referenceOrderId = response.referenceOrderNumber || ctx.item.referenceOrderId || '';
            const syncStatus = response.status ?? ctx.item.status ?? 20;

            console.log('Reference Order ID:', referenceOrderId);
            console.log('Sync Status:', syncStatus);

            // 👇 Check for orderType & requestedDate at top level
            const orderType = response.orderType ?? response.header?.orderType ?? null;
            const requestedDate = response.requestedDate ?? response.header?.requestedDate ?? null;

            console.log("Order Type:", orderType);
            console.log("Requested Date:", requestedDate);
                if (response && Array.isArray(response.content)) {
                    let itemList;
                    console.log('Processing order lines:', response.content);

                    if (ctx.division === '100' || ctx.division === '300') {
                        // Germany/Netherlands: use first record's VAT
                        itemList = response.content.map((item: any) => {
                    

                            return {
                                orderNumber: item.orderNumber || '',
                                orderLineId: item.orderLineId ?? item.orderId ?? null,
                                referenceOrderNumber: item.referenceOrderNumber || referenceOrderId || '',
                                lineNumber: item.lineNo.toString(),
                                itemName: item.itemName,
                                itemNumber: item.itemNo,
                                quantity: item.qty,
                                ...mapShipQtyFields(item),
                                rate: item.rate,
                                amount: item.grossAmount,
                                discountAmount: item.discountVal,
                                discount: clampDiscountPercent(item.discount),
                                amountAfterDiscount: item.netAmount,
                                vat: item.vat,
                                vatAmount: item.vatAmount,
                                amountAfterVat: item.lineAmount,
                                pcs: item.pcs,
                                size: item.size,
                                itemGroup: item.itemGroup,
                                netWeight: item.netWeight,
                                grossWeight: item.grossWeight,
                                vatCode: item.vatCode,
                                status: 20,
                                m3LineStatus: this.calculateM3LineStatus(
                                    item.m3LineStatus,
                                    syncStatus,
                                    referenceOrderId
                                ),
                                ...this.mapLineMarginFromApi(item),
                                noDecPl: resolveLineNoDecPl(item, 2)
                            };
                        });
                    } else if (ctx.division === '400') {
                        // After cutover: use second record's VAT if available
                        itemList = response.content.map((item: any, idx: number) => {
                        console.log("Item details2:", item);
                            return {
                                orderNumber: item.orderNumber || '',
                                orderLineId: item.orderLineId ?? item.orderId ?? null,
                                referenceOrderNumber: item.referenceOrderNumber || referenceOrderId || '',
                                lineNumber: item.lineNo.toString(),
                                itemName: item.itemName,
                                itemNumber: item.itemNo,
                                quantity: item.qty,
                                ...mapShipQtyFields(item),
                                rate: item.rate,
                                amount: item.grossAmount,
                                discountAmount: item.discountVal,
                                discount: clampDiscountPercent(item.discount),
                                amountAfterDiscount: item.netAmount,
                                vat: item.vat,
                                vatAmount: item.vatAmount,
                                amountAfterVat: item.lineAmount,
                                pcs: item.pcs,
                                size: item.size,
                                itemGroup: item.itemGroup,
                                netWeight: item.netWeight,
                                orderType: orderType, 
                                requestedDate: requestedDate, 
                                grossWeight: item.grossWeight,
                                vatCode: item.vatCode,
                                status: 20,
                                m3LineStatus: this.calculateM3LineStatus(
                                    item.m3LineStatus,
                                    syncStatus,
                                    referenceOrderId
                                ),
                                ...this.mapLineMarginFromApi(item),
                                noDecPl: resolveLineNoDecPl(item, 2)
                            };
                        });
                    } else {
                        // Default: use VAT from API, fallback to 0 if missing
                        itemList = response.content.map((item: any) => {
                          
                            return {
                                orderNumber: item.orderNumber || '',
                                orderLineId: item.orderLineId ?? item.orderId ?? null,
                                referenceOrderNumber: item.referenceOrderNumber || referenceOrderId || '',
                                lineNumber: item.lineNo.toString(),
                                itemName: item.itemName,
                                itemNumber: item.itemNo,
                                quantity: item.qty,
                                ...mapShipQtyFields(item),
                                rate: item.rate,
                                amount: item.gross,
                                discountAmount: item.discountVal,
                                discount: clampDiscountPercent(item.discount),
                                amountAfterDiscount: item.netAmount,
                                vat: item.vat,
                                vatAmount: item.vatAmount,
                                amountAfterVat: item.lineAmount,
                                pcs: item.pcs,
                                size: item.size,
                                itemGroup: item.itemGroup,
                                netWeight: item.netWeight,
                                grossWeight: item.grossWeight,
                                vatCode: item.vatCode,
                                status: 20,
                                m3LineStatus: this.calculateM3LineStatus(
                                    item.m3LineStatus,
                                    syncStatus,
                                    referenceOrderId
                                ),
                                ...this.mapLineMarginFromApi(item),
                                noDecPl: resolveLineNoDecPl(item, 2)
                            };
                        });
                    }

                    const creditNoteOrderType =
                        orderType ?? ctx.item?.orderTypeId ?? ctx.item?.orderType ?? '';
                    if (isCreditNoteOrder(ctx.division, creditNoteOrderType)) {
                        itemList = itemList.map((line: any) => {
                            const updated = {
                                ...line,
                                quantity: normalizeCreditNoteQuantity(Number(line.quantity))
                            };
                            return calculateRowAmounts(updated);
                        });
                    }

                    // Short-picked lines: recalc money from shipQty so V2 matches shipped reality
                    // without overwriting Ordered (quantity). Non-shipped lines keep API amounts.
                    itemList = itemList.map((line: any) =>
                        hasPersistedShipQty(line) ? calculateRowAmounts(line) : line
                    );

                    const applyFetchedItemList = (list: any[]) => {
                        ctx.itemListSetter(list);

                        if (list.length !== 0) {
                            ctx.messageHandler({
                                severity: 'success',
                                summary: 'Articles Fetched Successfully',
                                detail: 'Articles fetched successfully.',
                                life: 2000
                            });
                        } else {
                            ctx.messageHandler({
                                severity: 'warn',
                                summary: 'No Articles Found',
                                detail: 'No articles found for the selected order.',
                                life: 3000
                            });
                        }
                    };

                    applyFetchedItemList(itemList);
                } else {
                    ctx.loadingSetter(false);
                    ctx.messageHandler({
                        severity: 'error',
                        summary: 'Data Error',
                        detail: 'Received invalid data while fetching order articles.',
                        life: 3000
                    });
                }
            },
            error: (error: any) => {
                ctx.loadingSetter(false);
                ctx.messageHandler({
                    severity: 'error',
                    summary: 'Failed to Add Articles',
                    detail: 'Could not load articles. Please reload and try again.',
                    life: 3000
                });
            }
        });
    }
    getAndProcessItemDetails(ctx: {
        company: string;
        division: string;
        defaultPriceList?: string;
        body: any;
        priceLists: any[];
        warehouses: any[];
        info: any;
        qtySetter: (avlqty: number) => void;
        priceListsSetter: (lists: any[]) => void;
        warehousesSetter: (warehouses: any[]) => void;
        infoSetter: (info: any) => void;
        successHandler?: () => void;
        messageHandler: (msg: { severity: string; summary: string; detail: string; life?: number }) => void;
        loadingSetter: (val: boolean) => void; // <-- Add this line
        loaderMessageSetter: (l1: string, l2: string) => void;
        /** Filter DevTools by API-TRACE — e.g. ADD_ITEM, SELECT_ITEM, INSPECT_LINE */
        traceReason?: string;
    }) {
        const requestKey = JSON.stringify({
            company: ctx.company,
            division: ctx.division,
            body: ctx.body
        });
        if (requestKey === this.itemDetailsInFlightKey) {
            return;
        }
        this.itemDetailsSub?.unsubscribe();
        this.itemDetailsInFlightKey = requestKey;

        ctx.loadingSetter(true); // Start loading
        ctx.loaderMessageSetter('loading price lists and warehouses stock', 'Please wait...');
        this.itemDetailsSub = this.itemDetailsService
            .getItemDetails(ctx.company, ctx.division, ctx.body, ctx.traceReason || 'ADD_ITEM')
            .pipe(
                finalize(() => {
                    if (this.itemDetailsInFlightKey === requestKey) {
                        this.itemDetailsInFlightKey = null;
                    }
                })
            )
            .subscribe({
            next: (response) => {
                // Integrate VAT fields if present
                if (response && response.vatFields) {
                    ctx.info.vatDivision = response.vatFields.DIVI;
                    ctx.info.vatPercentage1 = response.vatFields.VTP1;
                    // ctx.info.vatPercentage2 = response.vatFields.VTP2;
                    ctx.info.vatFromDate = response.vatFields.FRDT;
                    ctx.info.vatCountryCode = response.vatFields.CSCD;
                    ctx.loadingSetter(false); // Stop loading
                }

                const marginResolution = resolveDivisionMarginFromItemDetailsResponse(
                    response,
                    ctx.division,
                    ctx.defaultPriceList
                );
                logDivisionMarginResolution(
                    `getAndProcessItemDetails item=${ctx.body?.itno} division=${ctx.division}`,
                    marginResolution
                );

                // Store ItemDetails fields if present
                if (response && response.ItemDetails) {
                    console.log('ItemDetails fields (API raw):', {
                        pcs: response.ItemDetails.pcs,
                        size: response.ItemDetails.size,
                        vatCode: response.ItemDetails.vatCode,
                        netWeight: response.ItemDetails.netWeight,
                        grossWeight: response.ItemDetails.grossWeight,
                        expiryDate: response.ItemDetails.expiryDate,
                        expiryStockQty: response.ItemDetails.expiryStockQty,
                        apiMCCSU1: response.ItemDetails.MCCSU1,
                        apiMFLAMA: response.ItemDetails.MFLAMA,
                        apiC1APPR: response.ItemDetails.C1APPR,
                        apiC2APPR: response.ItemDetails.C2APPR,
                        apiC3APPR: response.ItemDetails.C3APPR,
                        apiMILAMA: response.ItemDetails.MILAMA,
                        apiMTLAMA: response.ItemDetails.MTLAMA,
                        selectedPriceList: response.ItemDetails.selectedPriceList
                    });

                    ctx.info.pcs = response.ItemDetails.pcs;
                    ctx.info.size = response.ItemDetails.size;
                    ctx.info.vatCode = response.ItemDetails.vatCode;
                    ctx.info.netWeight = response.ItemDetails.netWeight;
                    ctx.info.grossWeight = response.ItemDetails.grossWeight;
                    ctx.info.expiryDate = response.ItemDetails.expiryDate;
                    ctx.info.expiryStockQty = response.ItemDetails.expiryStockQty;
                    ctx.info.itemGroup = response.ItemDetails.itemGroup;
                    ctx.info.AR = response.ItemDetails.STCD;
                    if (marginResolution) {
                        ctx.info.MCCSU1 = marginResolution.cost;
                        ctx.info.MFLAMA = marginResolution.minimumMargin;
                    } else {
                        ctx.info.MCCSU1 = response.ItemDetails.MCCSU1;
                        ctx.info.MFLAMA = response.ItemDetails.MFLAMA;
                    }
                    ctx.info.noDecPl = resolveQtyDecimalPlaces(extractNoDecPl(response.ItemDetails), 2);

                    console.log('ItemDetails margin stored in ctx.info:', {
                        division: ctx.division,
                        MCCSU1: ctx.info.MCCSU1,
                        MFLAMA: ctx.info.MFLAMA,
                        noDecPl: ctx.info.noDecPl,
                        resolvedFrom: marginResolution?.source,
                        costField: marginResolution?.costField,
                        marginField: marginResolution?.marginField
                    });
                }

                // Update price lists dynamically
                if (Array.isArray(response.lstPriceList)) {
                    response.lstPriceList.forEach((priceListItem: { ODPRRF: string; ODSAPR: number }) => {
                        const priceListName = priceListItem.ODPRRF;
                        const price = priceListItem.ODSAPR;
                        const existingPriceList = ctx.priceLists.find((pl) => pl.name === priceListName);
                        if (existingPriceList) {
                            existingPriceList.value = price;
                        } else {
                            ctx.priceLists.push({ name: priceListName, value: price });
                        }
                    });
                    ctx.priceListsSetter([...ctx.priceLists]);
                } else {
                    ctx.messageHandler({
                        severity: 'warn',
                        summary: 'Price List Warning',
                        detail: 'lstPriceList is empty or not valid.',
                        life: 3000
                    });
                }

                // Update warehouse quantity
                const warehouse = ctx.warehouses.find((wh) => wh.name === 'Warehouse');
                console.log("Update warehouse quantity",warehouse);
                
                let avlqty = 0;
                if (warehouse) {
                    warehouse.qty = Number(response.ItemDetails?.itemStockCount) || 0;
                    avlqty = warehouse.qty;
                    console.log(avlqty);
                    
                    ctx.qtySetter(avlqty);
                    ctx.warehousesSetter([...ctx.warehouses]);
                } else {
                    ctx.messageHandler({
                        severity: 'warn',
                        summary: 'Insufficient Stock',
                        detail: 'Insufficient stock for in Warehouse.',
                        life: 2000
                    });
                }

                if (!response.lstCoLnScrp) {
                    ctx.messageHandler({
                        severity: 'warn',
                        summary: 'Insufficient Stock',
                        detail: 'Insufficient stock for in Warehouse.',
                        life: 2000
                    });
                }

                // Update Latest Expiry Date in the info section
                if (response.getHead && response.getHead.RLDT) {
                    ctx.info.Date = response.getHead.RLDT;
                }

                // Update Last Sale Price from LstLastCOSAPR
                if (response.lstLastCOSAPR && response.lstLastCOSAPR.OBSAPR) {
                    ctx.info.latestExpiryDate = response.lstLastCOSAPR.OBSAPR;
                    ctx.info.lastInvoiceRate1 = response.lstLastCOSAPR.OBSAPR;
                    const obsapr = Number(response.lstLastCOSAPR.OBSAPR);
                    ctx.info.lastSalePrice = isNaN(obsapr) ? response.lstLastCOSAPR.OBSAPR : obsapr;
                } else {
                    ctx.info.lastSalePrice = 0;
                }
                ctx.infoSetter({ ...ctx.info });
                ctx.successHandler?.();
            },
            error: (error) => {
                ctx.loadingSetter(false); // Stop loading on error
                if (error.status >= 400) {
                    ctx.messageHandler({
                        severity: 'warn',
                        summary: 'Insufficient Stock',
                        detail: 'Insufficient stock for in Warehouse 100.',
                        life: 2000
                    });
                }
            }
        });
    }

    /** Apply stored order margin snapshot from GetOrderHeader without altering other header fields. */
    private applyHeaderMarginSnapshot(header: any): any {
        const orderMcm = Number(header?.orderMcm) || 0;
        if (orderMcm <= 0) {
            return header;
        }
        return { ...header, minimumOrderMargin: orderMcm };
    }

    /** Map line-level margin fields returned by GetListOrderLines. */
    private mapLineMarginFromApi(item: any): { cost?: number; lineCost?: number; minimumMargin?: number } {
        const mapped: { cost?: number; lineCost?: number; minimumMargin?: number } = {};
        if (item?.costPrice != null && item.costPrice !== '') {
            mapped.cost = Number(item.costPrice);
        }
        if (item?.lineCost != null && item.lineCost !== '') {
            mapped.lineCost = Number(item.lineCost);
        }
        if (item?.itemMcm != null && item.itemMcm !== '') {
            mapped.minimumMargin = Number(item.itemMcm);
        }
        return mapped;
    }

    /**
     * True only when localStorage has non-empty orderLines that belong to the same order as the header.
     * Prevents empty "[]" or stale drafts from skipping the pagination API.
     */
    private shouldUseOrderLinesDraftForOrder(currentOrderNumber: string): boolean {
        const raw = localStorage.getItem('orderLines');
        if (!raw || raw === '[]') {
            return false;
        }
        try {
            const lines = JSON.parse(raw);
            if (!Array.isArray(lines) || lines.length === 0) {
                return false;
            }
            const draftOrder = lines[0]?.orderNumber;
            if (!currentOrderNumber) {
                return false;
            }
            if (!draftOrder) {
                return false;
            }
            return String(draftOrder) === String(currentOrderNumber);
        } catch {
            return false;
        }
    }

    getAndProcessOrderHeader(ctx: {
        orderNumber: string;
        company: string;
        division: string;
        orderType: string;
        loadingSetter: (val: boolean) => void;
        productSetter: (product: any) => void;
        customerNameSetter: (name: string) => void;
        itemSetter: (item: any) => void;
        fetchOrderLines: () => void;
        readOnlySetter: (val: boolean) => void;
        itemNamesSetter: (names: string[]) => void;
        resetForm: () => void;
        itemList: any[];
        messageHandler: (msg: { severity: string; summary: string; detail: string; life: number }) => void;
    }) {
        ctx.loadingSetter(true);
        this.itemDetailsService.getOrderHeader(ctx.orderNumber, ctx.company, ctx.division, ctx.orderType).subscribe({
            next: (response) => {
                console.log('Order header fetched successfully:', response);
                ctx.loadingSetter(false);
                let address = '';
                let phoneNumber = '';
                let customerAddress = '';
                if (response.customerAddressDTOResponse) {
                    const addr = response.customerAddressDTOResponse;
                    address = [addr.addressLine1, addr.addressLine2, addr.addressLine3, addr.addressLine4].filter((line) => !!line && line.trim() !== '.').join(', ');
                    phoneNumber = addr.phoneNumber || '';
                    customerAddress = addr.combinedAddress || '';
                }

                const headerWithMargin = this.applyHeaderMarginSnapshot(response);
                ctx.productSetter(headerWithMargin);
                ctx.customerNameSetter(response.customerName || response.CUNM || response.customer || 'No customer selected');
                const item: any = {
                    ...headerWithMargin,
                    userName: response.userDto?.userName,
                    address: address,
                    phoneNumber: phoneNumber,
                    combinedAddress: customerAddress
                };
                item.customerId = response.customerId || response.CUNO || '';
                item.customerName = response.customerName || response.CUNM || '';
                if (response.uca2 != null && response.uca2 !== '') {
                    item.uca2 = response.uca2;
                }
                if (response.orderCost != null) {
                    item.orderCost = Number(response.orderCost);
                }
                ctx.itemSetter(item);
                // Only use localStorage draft when it has lines for THIS order. Otherwise "[]" or another
                // order's draft would skip GetListOrderLinesPagination and leave the grid blank (V1/V2).
                if (this.shouldUseOrderLinesDraftForOrder(item.orderNumber)) {
                    this.fetchOrderLinesDraft({
                        itemListSetter: (list: any[]) => {
                            ctx.itemList = list;
                        }
                    });
                    console.log('Using order lines from localStorage draft (same order number)');
                } else {
                    ctx.fetchOrderLines();
                    console.log('Fetching order lines from service');
                }
                const allowEdit = environment.allowEditingAfterInvoicing;
                console.log('Allow editing after invoicing:', allowEdit);

                // Your logic:
                let shouldBeReadOnly = false;
                if ((response.orderMovingStatus === 'INV' && !allowEdit) || (response.status >= 20 && response.orderMovingStatus === 'CRT')) {
                    shouldBeReadOnly = true;
                    console.log(response.status, response.orderMovingStatus, 'Setting readOnly to true');
                } else if (response.status >= 20) {
                    console.log(response.status, response.orderMovingStatus, 'Setting readOnly to true');
                    shouldBeReadOnly = true;
                }
                const immediateDivisions = (environment.divisionsToProcessImmediately || '').split(',').map((d) => d.trim());
                if (immediateDivisions.includes(ctx.division)) {
                    const status = response.status;
                    if (status !== 9 && status !== 10) {
                        shouldBeReadOnly = true;
                        console.log('Division', ctx.division, 'status', status, 'Setting readOnly to true (immediate division, only 9/10 editable)');
                    }
                }
                ctx.readOnlySetter(shouldBeReadOnly);
                // ctx.readOnlySetter(response.status >= 20);
                const itemNamesSet = new Set<string>((ctx.itemList || []).map((order) => order.itemName).filter((name): name is string => !!name));
                ctx.itemNamesSetter(Array.from(itemNamesSet));

                ctx.resetForm();
            },
            error: (err) => {
                ctx.loadingSetter(false);
                ctx.messageHandler({
                    severity: 'error',
                    summary: 'Order Fetch Failed',
                    detail: 'Failed to fetch order header. Please try again.',
                    life: 3000
                });
                console.error('Failed to fetch order header:', err);
            }
        });
    }
    //     fetchOrderLinesDraft(){
    //           const lines = JSON.parse(localStorage.getItem('orderLines') || '[]');
    //                     console.log('Order lines from localStorage:', lines);
    //                     let orderLines = [];
    //                     orderLines = lines || [];
    //                     let itemList;
    //                     console.log('Order lines from draft service:', orderLines);
    //                     itemList = (orderLines ?? []).map((item: any) => {
    //                         const gross = (item.qty ?? item.quantity ?? 0) * (item.rate ?? 0);
    //                         const discountVal = item.discountAmount || 0;
    //                         const net = gross - discountVal;
    //                         const vat = item.vat ?? item.vatPersentage ?? 0;
    //                         const vatAmount = (net * vat) / 100;
    //                         const lineAmount = net + vatAmount;
    // console.log('Order line processed from draft:', {
    // item
    // });

    //                         return {
    //                             lineNumber: item.lineNo.toString(),
    //                             itemName: item.itemName,
    //                             itemNumber: item.itemNo,
    //                             quantity: item.qty,
    //                             rate: item.rate,
    //                             amount: gross,
    //                             discountAmount: discountVal,
    //                             discount: item.discount,
    //                             amountAfterDiscount: net,
    //                             vat: vat,
    //                             vatAmount: vatAmount,
    //                             amountAfterVat: lineAmount,
    //                             pcs: item.pcs,
    //                             size: item.size,
    //                             itemGroup: item.itemGroup,
    //                             netWeight: item.netWeight,
    //                             grossWeight: item.grossWeight,
    //                             vatCode: item.vatCode,
    //                             status: 20
    //                         };
    //                     });
    //     }
    fetchOrderLinesDraft(ctx: {
        itemListSetter: (list: any[]) => void;
        // ...other ctx fields if needed
    }) {
        const lines = JSON.parse(localStorage.getItem('orderLines') || '[]');
        let itemList = (lines ?? []).map((item: any) => {
            const gross = round2((item.qty ?? item.quantity ?? 0) * (item.rate ?? 0));
            const discountVal = item.discountAmount || 0;
            const net = round2(gross - discountVal);
            const vat = item.vat ?? item.vatPersentage ?? 0;
            const vatAmount = round2((net * vat) / 100);
            const lineAmount = round2(net + vatAmount);
            console.log('Order line processed from draft:', {
                item
            });

            return {
                lineNumber: item.lineNo?.toString() || '',
                itemName: item.itemName,
                itemNumber: item.itemNo,
                quantity: item.qty,
                ...mapShipQtyFields(item),
                rate: item.rate,
                amount: gross,
                discountAmount: discountVal,
                discount: clampDiscountPercent(item.discount),
                amountAfterDiscount: net,
                vat: vat,
                vatAmount: vatAmount,
                amountAfterVat: lineAmount,
                pcs: item.pcs,
                size: item.size,
                itemGroup: item.itemGroup,
                netWeight: item.netWeight,
                grossWeight: item.grossWeight,
                vatCode: item.vatCode,
                status: 20,
                noDecPl: resolveLineNoDecPl(item, 2)
            };
        });
        // Set itemList in context
        ctx.itemListSetter(itemList);
    }

    // releaseOrderLock(orderNumber: string, companyId: string, divisionId: string, currentUsername: string): Observable<any> {
    //     const url = `${environment.apiUrl}orders/lock/release?orderNumber=${orderNumber}&companyId=${companyId}&divisionId=${divisionId}&currentUsername=${currentUsername}`;

    //     // const url = `http://192.168.31.92:8444/orders/lock/release?orderNumber=${orderNumber}&companyId=${companyId}&divisionId=${divisionId}&currentUsername=${currentUsername}`;
    //     return this.http.post(url, {}); // POST with empty body
    // }
    // refreshOrderLock(orderNumber: string, companyId: string, divisionId: string, currentUsername: string): Observable<any> {
    //     const url = `${environment.apiUrl}orders/lock/lockrefresh?orderNumber=${orderNumber}&companyId=${companyId}&divisionId=${divisionId}&currentUsername=${currentUsername}`;

    //     // const url = `http://192.168.31.92:8444/orders/lock/lockrefresh?orderNumber=${orderNumber}&companyId=${companyId}&divisionId=${divisionId}&currentUsername=${currentUsername}`;
    //     return this.http.post(url, {}); // POST with empty body
    // }
    // getPriceListInfo(customerId: string, companyId: string, divisionId: string, currentUsername: string): Observable<any> {
    //     const url = `${environment.apiUrl}m3/SearchCustomer/Details?customerId=${customerId}&company=${companyId}&division=${divisionId}&faci=${currentUsername}`;
    //     return this.http.get(url, {}); // POST with empty body
    // }
private calculateM3LineStatus(apiValue: any, _syncStatus: any, _referenceOrderId: any): string | null {
  if (apiValue !== null && apiValue !== undefined && apiValue !== '') {
    return String(apiValue);
  }
  return null;
}
}
